import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=checking, false=not auth, object=authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Skip if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const headers = {};
      const token = localStorage.getItem("trp_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      // `validateStatus` keeps 401 from polluting the browser console for
      // anonymous visitors — 401 here just means "not signed in", which is
      // the expected default state for the public site.
      const res = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers,
        validateStatus: (s) => s < 500,
      });
      // Backend now returns 200 with `{authenticated: false}` for anonymous
      // visitors (clean console) and 200 with the user doc for signed-in.
      if (res.status === 200 && res.data && res.data.email) {
        setUser(res.data);
        // Keep admin_token in localStorage so admin tabs (Roadmap, Ad Studio,
        // Invoicing, ...) survive page reloads. For non-admins make sure we
        // wipe any stale token left behind by a previous session.
        try {
          if (res.data.admin_token) {
            localStorage.setItem("admin_token", res.data.admin_token);
          } else if (res.data.role !== "admin") {
            localStorage.removeItem("admin_token");
          }
        } catch {}
      } else {
        if (res.status === 401) localStorage.removeItem("trp_token");
        setUser(false);
      }
    } catch {
      localStorage.removeItem("trp_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, totpCode) => {
    // Backend signals 2FA by returning {requires_2fa: true} (HTTP 200, no cookies).
    // We re-post with X-TOTP-Code header once the user has entered their code.
    const headers = totpCode ? { "X-TOTP-Code": totpCode } : {};
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true, headers });
    if (res.data?.requires_2fa) return res.data;  // caller shows the 2FA prompt
    if (res.data.access_token) localStorage.setItem("trp_token", res.data.access_token);
    // Persist admin_token so admin-gated tabs work right after a fresh login.
    try {
      if (res.data.admin_token) localStorage.setItem("admin_token", res.data.admin_token);
      else if (res.data.role !== "admin") localStorage.removeItem("admin_token");
    } catch {}
    setUser(res.data);
    return res.data;
  };

  const register = async (email, password, name) => {
    let ref_campaign = "";
    try {
      const rc = localStorage.getItem("trp_ref_campaign");
      const ts = parseInt(localStorage.getItem("trp_ref_campaign_ts") || "0", 10);
      // 30-day attribution window
      if (rc && ts && Date.now() - ts < 30 * 24 * 3600 * 1000) ref_campaign = rc;
    } catch {}
    const res = await axios.post(`${API}/auth/register`, { email, password, name, ref_campaign }, { withCredentials: true });
    try { localStorage.removeItem("trp_ref_campaign"); localStorage.removeItem("trp_ref_campaign_ts"); } catch {}
    if (res.data.access_token) localStorage.setItem("trp_token", res.data.access_token);
    try {
      if (res.data.admin_token) localStorage.setItem("admin_token", res.data.admin_token);
    } catch {}
    // Track CompleteRegistration in Meta Pixel + CAPI (deduped via event_id)
    try {
      const { trackEvent } = await import("../lib/metaPixel");
      trackEvent("CompleteRegistration", {
        email,
        content_name: "trp_account",
        content_category: ref_campaign || "organic",
      });
    } catch {}
    setUser(res.data);
    return res.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleCallback = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google`, { session_id: sessionId }, { withCredentials: true });
    try {
      if (res.data.admin_token) localStorage.setItem("admin_token", res.data.admin_token);
      else if (res.data.role !== "admin") localStorage.removeItem("admin_token");
    } catch {}
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem("trp_token");
    try { localStorage.removeItem("admin_token"); } catch {}
    setUser(false);
  };

  const updateProfile = async (data) => {
    const res = await axios.put(`${API}/auth/profile`, data, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, processGoogleCallback, logout, updateProfile, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
